<template>
  <div class="modal-content-wrapper">
    <div class="modal-header" :class="isClosed ? 'green-header' : 'red-header'">
      <h3 class="modal-title">{{ $t('gestionar_estado_ticket') }}</h3>
      <button type="button" class="btn-close" @click="closeModal">✕</button>
    </div>
    <div class="modal-body">
      <p>
        {{ isClosed ? $t('deseas_reabrir_ticket') : $t('deseas_cerrar_ticket') }}
        <strong>Ticket #{{ ticketId }}</strong>
      </p>
      <label for="descripcion" style="margin-top: 10px; display: block;">
        {{ isClosed ? $t('motivo_reapertura') : $t('motivo_cierre') }}
      </label>
      <textarea
        id="descripcion"
        v-model="descripcion"
        rows="3"
        style="width: 100%; margin-bottom: 10px;"
        :placeholder="$t('escribe_motivo')"
        required
      ></textarea>
      <div class="form-actions">
        <button
          type="button"
          :class="isClosed ? 'btn btn-danger' : 'btn btn-primary'"
          @click="handleAction"
          :disabled="!descripcion.trim() || loading"
        >
          <span v-if="loading">{{ $t('actualizando') || 'Actualizando...' }}</span>
          <span v-else>{{ isClosed ? $t('reabrir_ticket') : $t('cerrar_ticket') }}</span>
        </button>
        <button type="button" class="btn btn-secondary" @click="closeModal">
          {{ $t('cancelar') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../api';
import { showAlert } from '@/utils/alerts';

export default {
  name: 'OpenCloseTicketModalView',
  emits: ['close', 'submit'],
  props: {
    ticketId: {
      type: Number,
      required: true,
    },
    isClosed: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      descripcion: '',
      loading: false,
    };
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    
    async handleAction() {
      if (this.loading) return;
      this.loading = true;
      
      try {
        console.log('Abriendo/cerrando ticket:', this.ticketId);
        
        // 1. Registrar motivo de cierre/reapertura
        await api.post('/ticket-cierre', {
          id_ticket: this.ticketId,
          descripcion: this.descripcion,
          esCierre: !this.isClosed,
          es: true
        });

        // 2. Cambiar estado del ticket
        const endpoint = this.isClosed
          ? `/tickets/${this.ticketId}/reopen`
          : `/tickets/${this.ticketId}/close`;

        const response = await api.put(endpoint, {});

        console.log('Respuesta del servidor:', response.data);

        // 3. Preparar datos para emitir
        const updatedTicket = {
          id: this.ticketId,
          estado: this.isClosed ? 'Abierto' : 'Cerrado',
          ...response.data
        };

        // 4. Emitir evento de éxito
        this.$emit('submit', updatedTicket);
        
        // 5. Mostrar alerta de éxito
        const title = this.isClosed 
          ? (this.$t('ticket_reabierto') || 'Ticket reabierto') 
          : (this.$t('ticket_cerrado') || 'Ticket cerrado');
        
        const message = this.isClosed
          ? (this.$t('swal_estado_actualizado_text', { id: this.ticketId }) || `El ticket #${this.ticketId} ha sido reabierto.`)
          : (this.$t('swal_estado_actualizado_text', { id: this.ticketId }) || `El ticket #${this.ticketId} ha sido cerrado.`);
        
        await showAlert('success', title, message);
        
      } catch (error) {
        console.error('Error en el flujo de cierre/reapertura:', error.response?.data || error.message);
        
        const errorTitle = this.$t('sweetalert_error_title') || 'Error';
        const errorMessage = this.$t('sweetalert_ticket_error', { id: this.ticketId }) || `Error al procesar el ticket #${this.ticketId}`;
        
        await showAlert('error', errorTitle, `${errorMessage}: ${error.response?.data?.message || error.message}`);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-content-wrapper {
  background-color: white;
  border-radius: 8px;
  width: 420px;           /* Ajusta el ancho del modal */
  max-width: 98vw;        /* Mejor adaptación en pantallas pequeñas */
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  box-sizing: border-box;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 18px;     /* Ajusta el padding para cabecera más compacta */
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  width: 100%;            /* Cabecera ocupa todo el ancho del modal */
  box-sizing: border-box;
  margin-top: 0;
  left: 0;
}

.modal-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: bold;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: white;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0.8;
}

.btn-close:hover {
  opacity: 1;
}

.modal-body {
  padding: 18px;          /* Ajusta el padding para cuerpo más compacto */
  overflow-x: hidden;
  overflow-y: auto;
  max-width: 100%;        /* El cuerpo ocupa todo el ancho disponible */
  box-sizing: border-box;
  max-height: none;
}

label {
  font-weight: bold;
  color: #333;
  display: block;
  margin-bottom: 8px;
}

textarea {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px;
  font-size: 1rem;
  resize: none;
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
}

textarea:focus {
  outline: none;
  border-color: #99172A;
  box-shadow: 0 0 0 2px rgba(153, 23, 42, 0.2);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.btn {
  padding: 10px 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  transition: background-color 0.3s ease;
  min-width: 120px;
}

.btn-primary {
  background-color: #28a745;
  color: white;
}

.btn-primary:hover:not([disabled]) {
  background-color: #218838;
}

.btn-primary[disabled] {
  background-color: #6c757d !important;
  color: #ccc;
  cursor: not-allowed;
  opacity: 0.7;
}

.btn-danger {
  background-color: #99172A;
  color: white;
}

.btn-danger:hover:not([disabled]) {
  background-color: #7A1222;
}

.btn-danger[disabled] {
  background-color: #6c757d !important;
  color: #ccc;
  cursor: not-allowed;
  opacity: 0.7;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

.red-header {
  background-color: #99172A;
  color: white;
}

.green-header {
  background-color: #28a745;
  color: white;
}

@media (max-width: 900px) {
  .modal-content-wrapper {
    width: calc(100% - 32px) !important;
    max-width: calc(100% - 32px) !important;
  }
  
  .modal-title {
    font-size: 1.2rem;
  }
  
  .btn {
    padding: 8px 16px;
    min-width: 100px;
    font-size: 14px;
  }
}
</style>